import { useEffect, useRef, useState } from "react";
import { Listen, Prompt } from "../components/Audio";
import { CheckIcon } from "../components/Icons";
import ItemCard from "../components/ItemCard";
import Picture from "../components/Picture";
import ui from "../components/ui.module.css";
import useSentBack from "../hooks/useSentBack";


const nameOf = (item, index) => item.label || item.asset?.alt || `Picture ${index + 1}`;

// Tap a picture, then the group it belongs in. A placed picture can be tapped
// again and moved to another group until Done. The finished groups are handed
// in as they are; the gateway judges and policy decides what happens next.
const restored = (saved) =>
  Object.fromEntries(
    (Array.isArray(saved?.assignments) ? saved.assignments : []).map((a) => [a.item, a.group])
  );

const SortingStep = ({ step, saved, verdict, onDone, saving, readOnly = false }) => {
  const [placed, setPlaced] = useState(() => (readOnly ? restored(saved) : {})); // item value → group value
  const [picked, setPicked] = useState(null); // item value
  // "sent" once handed in; "wrong" while sent-back groups are shown.
  const [result, setResult] = useState(readOnly ? "sent" : null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useSentBack(verdict, () => setResult("wrong"));

  const locked = saving || result === "sent";

  const pick = (value) => {
    if (locked) return;
    setResult(null);
    setPicked((current) => (current === value ? null : value));
  };

  const drop = (group) => {
    if (locked || !picked) return;
    setPlaced((current) => ({ ...current, [picked]: group }));
    setPicked(null);
  };

  const complete = step.items.every((item) => placed[item.value]);

  const check = () => {
    if (!complete || locked) return;
    // Saved in the authored order of the pictures; the order means nothing.
    const assignments = step.items.map(({ value }) => ({
      item: value,
      group: placed[value],
    }));
    setResult("sent");
    onDone({ assignments });
  };

  const card = (item) => {
    const index = step.items.indexOf(item);
    const isPicked = picked === item.value;
    return (
      <li key={item.value}>
        <ItemCard
          item={item}
          className={[
            ui.choice,
            ui.sortCard,
            isPicked && ui.picked,
            verdict?.kind === "right" && ui.choiceRight,
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => pick(item.value)}
          aria-pressed={isPicked}
          aria-label={nameOf(item, index)}
          disabled={saving}
        >
          <Picture item={item} />
        </ItemCard>
      </li>
    );
  };

  const waiting = step.items.filter((item) => !placed[item.value]);

  return (
    <>
      <Prompt step={step} />

      <div className={`${ui.groups} ${result === "wrong" ? ui.shake : ""}`}>
        {step.groups.map((group, index) => {
          const members = step.items.filter((item) => placed[item.value] === group.value);
          const name = nameOf(group, index);
          return (
            <section
              key={group.value}
              className={`${ui.group} ${picked ? ui.groupReady : ""}`}
              aria-label={name}
            >
              <button
                type="button"
                className={ui.groupTarget}
                onClick={() => drop(group.value)}
                disabled={locked || !picked}
                aria-label={`Put in ${name}`}
              >
                <Picture item={group} />
              </button>
              <Listen audio={group.audio} label={group.label} small />
              <ul className={ui.groupItems}>{members.map(card)}</ul>
            </section>
          );
        })}
      </div>

      {waiting.length > 0 && <ul className={ui.choices}>{waiting.map(card)}</ul>}

      <button
        type="button"
        className={`${ui.button} ${ui.large} ${ui.primary}`}
        onClick={check}
        disabled={!complete || locked}
      >
        <CheckIcon size={24} />
        {saving ? "Saving…" : "Done"}
      </button>
    </>
  );
};

export default SortingStep;
